import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ---------------------------------------------------------------------------
// Retry policy for transient infrastructure failures.
// ---------------------------------------------------------------------------
// Why this exists:
//   The backend runs under `uvicorn --reload`. Saving a backend file causes
//   a 5-15s restart window where ingress returns 502/503 (or no response at
//   all). End-users see "login failed" or "noe gikk galt" even though their
//   credentials are valid. We've shipped fixes during HMR three times now —
//   this interceptor makes the SPA itself resilient so a brief backend bounce
//   is invisible to humans.
//
// Rules:
//   - Retry ONLY on transient infra signals (network drop, 502, 503, 504,
//     timeout). Never on 4xx — those are real application responses.
//   - Max 3 retries with a fixed 1s backoff = total budget ~3-4s.
//   - Skip retry on the SSE/streaming or long-poll endpoints if any.

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const TRANSIENT_STATUSES = new Set([502, 503, 504]);

const isTransientError = (error) => {
  // No response object → network error, DNS, TLS, preflight blocked
  if (!error.response) {
    const code = error.code || '';
    return code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT' || code === '';
  }
  return TRANSIENT_STATUSES.has(error.response.status);
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const apiClient = axios.create({
  baseURL: API_URL,
  // 30s default timeout — long enough for slow uploads, short enough that
  // a wedged request still surfaces an error within human attention span.
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // ---------- Transient retry layer ----------
    // Decide *first* whether to retry. If yes, schedule + replay the request
    // before any of the application-level handling (401 redirect, 422 detail
    // flattening) takes effect. A successful retry should look like the call
    // simply took a beat longer.
    const config = error.config;
    if (config && isTransientError(error) && !config.__skipRetry) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < MAX_RETRIES) {
        config.__retryCount += 1;
        await sleep(RETRY_DELAY_MS);
        return apiClient.request(config);
      }
    }
    // Auto-flatten FastAPI 422 detail arrays to a single readable string so
    // call-sites never need to special-case Pydantic errors. Without this,
    // `error.response.data.detail` is an array of {type, loc, msg, input, url}
    // objects, and rendering it as a React child crashes the app.
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = detail
        .map((e) => {
          const field = Array.isArray(e?.loc) ? e.loc.slice(1).join('.') : '';
          return field ? `${field}: ${e?.msg || ''}` : e?.msg || '';
        })
        .filter(Boolean)
        .join(' · ');
    }

    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      // Don't redirect if the user is already on a public page (login, signup,
      // forgot password, reset). Otherwise we risk loops on bad creds errors.
      const path = window.location.pathname;
      const isAuthPage = path === '/login' || path === '/signup'
        || path === '/forgot-password' || path.startsWith('/reset-password');
      if (!isAuthPage) {
        // Surface `password_changed` so /login can render a friendly toast
        // ("You have changed your password — please log in again.").
        const reason = error.response?.data?.detail === 'password_changed' ? 'password_changed' : '';
        const qs = reason ? `?reason=${reason}` : '';
        window.location.href = `/login${qs}`;
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// ---------------------------------------------------------------------------
// authClient — used by AuthContext + InvestorsPage gate.
// ---------------------------------------------------------------------------
// Mirrors `apiClient`'s retry policy but omits the 401-redirect (auth endpoints
// are SUPPOSED to return 401 on bad creds, and redirecting from /login back
// to /login would be an awful UX). Keeping these flows on a shared instance
// would also fight with the redirect, so we mint a sibling instance instead.

export const authClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

authClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (config && isTransientError(error) && !config.__skipRetry) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < MAX_RETRIES) {
        config.__retryCount += 1;
        await sleep(RETRY_DELAY_MS);
        return authClient.request(config);
      }
    }
    return Promise.reject(error);
  },
);

export const profilesAPI = {
  getMyProfile: () => apiClient.get('/api/profiles/me'),
  requestSectorChange: (data) => apiClient.post('/api/profiles/me/sector-change-request', data),
  getSectorChangeRequest: () => apiClient.get('/api/profiles/me/sector-change-request'),
  updateProfile: (data) => apiClient.put('/api/profiles/me', data),
  searchProfiles: () => apiClient.get('/api/profiles/search')
};

export const organizationsAPI = {
  getMyOrganization: () => apiClient.get('/api/organizations/me'),
  createOrganization: (data) => apiClient.post('/api/organizations', data),
  updateOrganization: (data) => apiClient.put('/api/organizations/me', data)
};

export const jobsAPI = {
  createJob: (data) => apiClient.post('/api/jobs', data),
  listJobs: () => apiClient.get('/api/jobs'),
  getJob: (jobId) => apiClient.get(`/api/jobs/${jobId}`),
  saveJob: (jobId) => apiClient.post(`/api/jobs/${jobId}/save`),
  unsaveJob: (jobId) => apiClient.delete(`/api/jobs/${jobId}/save`),
  getSavedJobs: () => apiClient.get('/api/me/saved-jobs'),
  updateTranslation: (jobId, data) => apiClient.put(`/api/jobs/${jobId}/translation`, data),
  regenerateTranslation: (jobId) => apiClient.post(`/api/jobs/${jobId}/translate`),
  updateJob: (jobId, data) => apiClient.put(`/api/jobs/${jobId}`, data),
  closeJob: (jobId) => apiClient.post(`/api/jobs/${jobId}/close`)
};

export const applicationsAPI = {
  applyToJob: (data) => apiClient.post('/api/applications', data),
  getMyApplications: () => apiClient.get('/api/applications/my'),
  getApplicationsByJob: (jobId) => apiClient.get(`/api/applications/by_job/${jobId}`),
  updateStatus: (applicationId, status, note) =>
    apiClient.patch(`/api/applications/${applicationId}/status`, {
      status,
      ...(note ? { note } : {}),
    }),
  getAll: () => apiClient.get('/api/applications')
};

export const matchingAPI = {
  getMatchedJobs: () => apiClient.get('/api/match/jobs_for_me'),
  getCandidatesForJob: (jobId) => apiClient.get(`/api/match/candidates_for_job/${jobId}`)
};

export const messagingAPI = {
  createConversation: (candidateUserId, initialMessage) =>
    apiClient.post('/api/conversations', {
      candidate_user_id: candidateUserId,
      initial_message: initialMessage,
    }),
  listConversations: () => apiClient.get('/api/conversations'),
  getUnreadCount: () => apiClient.get('/api/conversations/unread-count'),
  getMessages: (conversationId, { limit, before } = {}) =>
    apiClient.get(`/api/conversations/${conversationId}/messages`, {
      params: { ...(limit ? { limit } : {}), ...(before ? { before } : {}) },
    }),
  sendMessage: (conversationId, body) =>
    apiClient.post(`/api/conversations/${conversationId}/messages`, { body }),
  sendInterviewInvite: (conversationId, slots, note) =>
    apiClient.post(`/api/conversations/${conversationId}/interview-invite`, {
      slots,
      ...(note ? { note } : {}),
    }),
  acceptInterviewInvite: (messageId, slotId) =>
    apiClient.post(`/api/messages/${messageId}/interview-accept`, { slot_id: slotId }),
  rescheduleInterview: (messageId, slots, note) =>
    apiClient.post(`/api/messages/${messageId}/interview-reschedule`, {
      slots,
      ...(note ? { note } : {}),
    }),
  getUpcomingInterviews: () => apiClient.get('/api/me/upcoming-interviews'),
  archiveConversation: (conversationId) =>
    apiClient.delete(`/api/conversations/${conversationId}`),
};

export const aiAPI = {
  getLocationInsight: (place, context, language) => apiClient.get('/api/ai/location_insight', { params: { place, context, language } }),
  generateDescription: (place, type) => apiClient.post('/api/ai/generate_description', null, { params: { place, type } })
};

// Phase 3.1: Profile Completeness
export const profileCompletenessAPI = {
  getCompleteness: () => apiClient.get('/api/profiles/completeness')
};

// Phase 3.1: Advanced Job Search
export const jobSearchAPI = {
  search: (params) => apiClient.get('/api/jobs/search', { params }),
  getFilterOptions: () => apiClient.get('/api/jobs/filter-options')
};

// Phase 3.1: GDPR Compliance
export const authAPI = {
  changePassword: (data) => apiClient.post('/api/auth/change-password', data),
};

export const gdprAPI = {
  exportData: () => apiClient.get('/api/users/export_data'),
  deleteAccount: (data) => apiClient.post('/api/users/delete_account', data),
  getNotificationPreferences: () => apiClient.get('/api/users/notification_preferences'),
  updateNotificationPreferences: (prefs) => apiClient.put('/api/users/notification_preferences', prefs)
};
